/**
 * #1006 — what a US cancel result may be called.
 *
 * A US cancel reads the order, POSTs the cancellation, then reads the order
 * again. Anything that goes wrong after the POST may leave a cancellation the
 * relay already accepted, and the relay answers every refused or uncertain
 * cancel with 503 `US_REQUEST_UNCONFIRMED` (#1005). So "failed" is reserved
 * for results that prove nothing went out; everything else is "unconfirmed",
 * and nothing here is ever reported as a cancelled order (Brendan, Sep 29:
 * "keep unconfirmed separate from failed, so nobody reads it as done").
 */
import type { UsCancelResult } from './types';

/** Codes the relay/client use for a cancel whose outcome is not known. */
const UNCONFIRMED_CODES: ReadonlySet<string> = new Set(['US_REQUEST_UNCONFIRMED', 'US_CANCEL_UNCONFIRMED']);

/** Client refusals raised before the cancellation POST (portfolioClient.cancel). */
const NOT_SENT_CODES: ReadonlySet<string> = new Set([
  'US_CLIENT_INVALID_REQUEST',
  'US_CLIENT_INVALID_SCOPE',
  'US_CLIENT_ORDER_CHANGED',
  'US_CLIENT_CANCEL_PENDING',
  'US_CLIENT_AUTH_REQUIRED',
]);

export const US_CANCEL_UNCONFIRMED_TEXT = 'Cancel unconfirmed — check status before trying again';

/** True for the explicit "outcome unknown" codes, e.g. to word a row message. */
export function isUsCancelUnconfirmedCode(code: string): boolean {
  return UNCONFIRMED_CODES.has(code);
}

export type UsCancelOutcome = 'requested' | 'unconfirmed' | 'failed';

/** A cancel call that threw. Only a failure the client proves came before
 * the cancellation POST (`cancelNotSent`, or a pre-POST client refusal code)
 * is `failed`; the same code after the POST stays unconfirmed. */
export function usCancelErrorOutcome(error: unknown): Exclude<UsCancelOutcome, 'requested'> {
  if (error && typeof error === 'object' && 'cancelNotSent' in error && error.cancelNotSent === true) return 'failed';
  const code = error && typeof error === 'object' && 'code' in error && typeof error.code === 'string' ? error.code : '';
  return NOT_SENT_CODES.has(code) ? 'failed' : 'unconfirmed';
}

/** A cancel call that returned: the server's active attempt decides. An
 * acknowledged cancel is still not a cancelled order. */
export function usCancelResultOutcome(result: UsCancelResult): UsCancelOutcome {
  switch (result.attempt.state) {
    case 'dispatching':
    case 'acknowledged':
      return 'requested';
    case 'unknown':
      return 'unconfirmed';
    case 'not_sent':
      return 'failed';
  }
}
